import { keepPreviousData } from '@tanstack/react-query';

import Empty from 'components/Empty';
import FrameGrid from 'components/FrameGrid';
import FrameGridItem from 'components/FrameGridItem';
import NftGridItem from 'components/NftGridItem';
import Box from 'components/base/Box';
import ButtonWithClose from 'components/base/ButtonWithClose';
import ButtonWithCount from 'components/base/ButtonWithCount';
import Flex from 'components/base/Flex';
import InfiniteScrollButton from 'components/feed/InfiniteScrollButton';
import MarketAvailabilityFilter from 'components/filters/MarketAvailabilityFilter';
import NftsSort from 'components/filters/NftsSort';
import FilterLayout from 'components/profiles/tabs/FilterLayout';
import FiltersSkeleton from 'components/profiles/tabs/FiltersSkeleton';

import { useInfiniteNftsSearch } from 'gql/api/queries/nfts-search.generated';
import { CollectionFragmentExtended } from 'gql/hasura/hasura-fragments.generated';
import { useFilterStateAtom } from 'hooks/filters/use-nft-filters';
import { useNftFiltersUrlSync } from 'hooks/filters/use-nft-filters-url-sync';
import useCollectionArtworkCreator, {
  CollectionArtworkCreator,
} from 'hooks/queries/hasura/collections/use-collection-artwork-creator';
import { ChainId } from 'lib/chains';
import { HASURA_DEFAULT_ITEMS_PER_PAGE } from 'lib/constants';
import { isSharedContract } from 'utils/collections';
import { getUsernameOrTruncatedAddress } from 'utils/helpers';
import { mapApiMediaToPreviewMediaAsset } from 'utils/media-preview';
import { apiPaginator } from 'utils/react-query';

type CollectionArtworksCollection = Pick<
  CollectionFragmentExtended,
  'contractAddress' | 'contractType'
>;

interface CollectionArtworksProps {
  collectionChainId: ChainId;
  collection: CollectionArtworksCollection;
  artworkCreator?: CollectionArtworkCreator;
  isOwnerOnCollection: boolean;
  isOwnerOrAdmin: boolean;
  burnedTokensCount: number | null;
  mintedTokensCount: number | null;
  unstyledCollection: boolean;
  isEmptyCollection: boolean;
}

type CollectionArtworksRootProps = CollectionArtworksProps;

export function CollectionArtworks(props: CollectionArtworksRootProps) {
  if (isSharedContract(props.collection.contractType)) {
    return <SharedCollectionArtworks {...props} />;
  }

  return <CollectionArtworksV2 {...props} />;
}

function SharedCollectionArtworks(props: CollectionArtworksRootProps) {
  const filtersApi = useFilterStateAtom({
    cacheKey: props.collection.contractAddress,
    initialValues: {
      sortOrder: 'MOST_ACTIVE',
    },
  });

  useNftFiltersUrlSync(filtersApi);

  const artworkCreatorQuery = useCollectionArtworkCreator({
    contractAddress: props.collection.contractAddress,
    // @ts-expect-error null-checks
    artworkCreatorPublicKey: filtersApi.filters.creatorPublicKey,
  });

  return (
    <CollectionArtworksV2
      artworkCreator={artworkCreatorQuery.data}
      {...props}
    />
  );
}

export function CollectionArtworksV2(props: CollectionArtworksProps) {
  const filtersApi = useFilterStateAtom({
    cacheKey: props.collection.contractAddress,
    initialValues: {
      sortOrder: 'MOST_ACTIVE',
    },
  });

  const infiniteNftsSearchQuery = useInfiniteNftsSearch(
    {
      searchTerm: '',
      accountAddress: props.artworkCreator
        ? props.artworkCreator.publicKey
        : undefined,
      collectionAddresses: [props.collection.contractAddress],
      page: 0,
      perPage: HASURA_DEFAULT_ITEMS_PER_PAGE,
      sortOrder: filtersApi.filters.sortOrder,
      marketAvailability: filtersApi.filters.marketAvailability,
    },
    {
      placeholderData: keepPreviousData,
      getNextPageParam: (lastPage) => {
        if (lastPage.nftsSearchV2Db.search.items.length === 0) {
          return null;
        }
        return {
          page: lastPage.nftsSearchV2Db.search.page + 1,
        };
      },
      initialPageParam: apiPaginator.initialPageParam,
    }
  );

  if (!infiniteNftsSearchQuery.isSuccess) {
    return (
      <FilterLayout filters={<FiltersSkeleton />}>
        <FrameGrid.Root maxColumns={4}>
          {/* row 1 */}
          <FrameGridItem.Skeleton />
          <FrameGridItem.Skeleton />
          <FrameGridItem.Skeleton />
          <FrameGridItem.Skeleton />
          {/* row 2 */}
          <FrameGridItem.Skeleton />
          <FrameGridItem.Skeleton />
          <FrameGridItem.Skeleton />
          <FrameGridItem.Skeleton />
        </FrameGrid.Root>
      </FilterLayout>
    );
  }

  const nfts = infiniteNftsSearchQuery.data.pages.flatMap(
    (page) => page.nftsSearchV2Db.search.items
  );

  const filters = (
    <div>
      <Flex
        css={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '$2',
        }}
      >
        <Box
          css={{
            // TODO: find a way to reuse these styles, with dynamic media queries
            gap: '$2',
            width: '100%',

            '@bp0-max': {
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              flexGrow: 1,

              [`& ${ButtonWithCount.Root}`]: {
                justifyContent: 'space-between',
              },
            },

            '@bp0': {
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              width: 'auto',
            },
          }}
        >
          <MarketAvailabilityFilter
            filters={filtersApi.filters}
            setMarketAvailability={filtersApi.setMarketAvailability}
          />
        </Box>
        <Flex css={{ marginLeft: 'auto' }}>
          <NftsSort
            filters={filtersApi.filters}
            setFilters={filtersApi.setFilters}
          />
        </Flex>
      </Flex>
      {props.artworkCreator && (
        <Flex css={{ marginTop: '$2' }}>
          <ButtonWithClose
            onClick={() => {
              filtersApi.resetCreatorFilter();
            }}
            size={0}
          >
            {getUsernameOrTruncatedAddress(props.artworkCreator)}
          </ButtonWithClose>
        </Flex>
      )}
      {filtersApi.filters.media &&
        props.collection.contractType !== 'FND_BATCH_MINT_REVEAL' && (
          <Flex css={{ marginTop: '$2' }}>
            <ButtonWithClose
              onClick={() => {
                filtersApi.resetFilters();
              }}
              size={0}
            >
              Selected
            </ButtonWithClose>
          </Flex>
        )}
    </div>
  );

  return (
    <div>
      {nfts.length === 0 ? (
        <FilterLayout filters={filters}>
          <Empty.WithFilters
            message="Adjust the selected filters to see more NFTs."
            onClear={() => {
              filtersApi.resetFilters();
            }}
          />
        </FilterLayout>
      ) : (
        <FilterLayout filters={filters}>
          <FrameGrid.Root
            css={{
              maxWidth: '$container',
              transition: 'opacity $2 $ease',
              opacity: infiniteNftsSearchQuery.isRefetching ? 0.5 : 1,
            }}
            maxColumns={4}
          >
            {nfts.map((nft) => {
              const media = nft.media
                ? mapApiMediaToPreviewMediaAsset(nft.media)
                : null;
              const optimizedMedia = media
                ? NftGridItem.optimizeMedia(media, { maxColumns: 4 })
                : null;

              return (
                <NftGridItem
                  key={nft.id}
                  nft={nft}
                  media={optimizedMedia}
                  filterContext={
                    filtersApi.filters.marketAvailability || undefined
                  }
                />
              );
            })}
          </FrameGrid.Root>
        </FilterLayout>
      )}

      <InfiniteScrollButton
        handleNextPage={infiniteNftsSearchQuery.fetchNextPage}
        isFetching={infiniteNftsSearchQuery.isFetchingNextPage}
        hasNextPage={infiniteNftsSearchQuery.hasNextPage}
      />
    </div>
  );
}
